import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import I18n from '@/modules/i18n';
import Dropdown from '@/components/common/Dropdown';
import useSlideShowStore, { ISlideOrSlideGroup } from '@/components/admin/SlideShow/stores/useSlideShowStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { inIframe } from '@/modules/TCIUtils';
import useFlags from '@/hooks/useFlags';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import { UnstyledButton } from '@/components/ui/Buttons';
import styles from '../../Navigator.module.scss';
import Switch from './Switch';
import FullScreen, { FullScreenPropTypes } from './FullScreen';
import { autoplayMediaSetting, slidesHaveAutoplayAudioOrVideo } from '../../../Utils';
import SlideShowEditButton, { SlideShowEditButtonPropTypes } from './SlideShowEditButton';
import ResetSlideShowButton from './ResetSlideShowButton/ResetSlideShowButton';
import LiveSessionControl from './LiveSessionControl';

export const useSettings = (storageKey, initialState = {}) => {
  const [settings, setSettings] = useState(initialState);

  const currentSettings = () => JSON.parse(localStorage.getItem(storageKey)) || {};

  const update = (key, value) => {
    setSettings({ ...settings, [key]: value });
    localStorage.setItem(storageKey, JSON.stringify({ ...currentSettings(), [key]: value }));
  };

  const toggleAutoplayMedia = (newValue, slideShowId) => {
    const autoplayMediaSettings = currentSettings().autoplayMedia || {};
    autoplayMediaSettings[slideShowId] = newValue;
    update('autoplayMedia', autoplayMediaSettings);
  };

  useEffect(() => {
    setSettings(currentSettings() || initialState);
  }, [storageKey]);

  return { settings, update, toggleAutoplayMedia };
};

const Settings = ({
  fullScreenProps,
  slideShowEditButtonProps,
  settings,
  slideShow,
  toggleAutoplayMediaEnabled,
  update,
}) => {
  const activityType = useSlideShowContext(state => state.activityType);
  const userType = useSlideShowContext(state => state.userType);
  const stafferId = useSlideShowContext(state => state.userId);
  const programId = useSlideShowContext(state => state.programId);
  const slideShowFromStore = useSlideShowStore(s => s.slideShow) ?? useSlideShowContext(s => s.slideShow);
  const slides: ISlideOrSlideGroup[] = useSlideShowContext(state => state.slides);
  const isProfessionalDevelopment = useSlideShowStore(state => (
    state.slideShow.activityType === 'professional_development'
  ));
  const canViewSlideConditionsToggle = [
    'Admin', 'ContentManager', 'Coordinator', 'Sysadmin', 'Teacher',
  ].includes(userType);
  const isResettable = useSlideShowContext(state => state.isResettable);
  const { autoplayAudio, teacherPacedMode } = useFlags();
  const { autoplayMedia: autoplayMediaSettings = {} } = settings;

  const resettableSlideShowTypes = [
    'video_activity', 'professional_development', 'classroom_activity', 'lesson_outline',
  ];

  const showResetButton = () => {
    if (userType === 'Student') return isResettable;

    return resettableSlideShowTypes.includes(activityType);
  };

  // When flag is removed, simplify css in Navigator.module.scss, removing 'legacy' and merging in 'teacherPacedUpdates'
  // so that each element has just one classname
  if (teacherPacedMode) {
    return (
      <Dropdown
        ariaLabel={I18n.t('slide_show_settings')}
        buttonClass={styles.settingsButton}
        containerClass={styles.settingsDropdownContainer}
        dropdownClass={cn(styles.settingsDropdown, styles.teacherPacedUpdates)}
        dropdownPosition="top-end"
        render={({ closeModal, isOpen }) => (
          <React.Fragment>
            <p className={cn(styles.dropdownHeader, styles.teacherPacedUpdates)}>
              Settings:
              <UnstyledButton aria-label={I18n.t('close')} type="button" onClick={closeModal}>
                <Icon color="#777770" icon="Close" size={16} />
              </UnstyledButton>
            </p>
            {userType !== 'Student' && (
              <LiveSessionControl
                programId={programId}
                stafferId={stafferId}
              />
            )}
            {!inIframe() && userType !== 'Student' && activityType !== 'marketing' && !isProfessionalDevelopment && (
              <SlideShowEditButton {...slideShowEditButtonProps} slideShow={slideShow} />
            )}
            {isOpen && showResetButton() && <ResetSlideShowButton />}
            <FullScreen {...fullScreenProps} />
            {slideShowFromStore && slideShowFromStore.hasSlideConditions && canViewSlideConditionsToggle && (
              <div className={cn(styles.settingsSwitchRow, styles.teacherPacedUpdates)}>
                <Switch
                  checked={settings.overrideSlideConditions}
                  id="overrideSlideConditions"
                  label={I18n.t('slide_conditions_override')}
                  tooltip={I18n.t('slide_conditions_override_tooltip')}
                  onChange={newValue => update('overrideSlideConditions', newValue)}
                />
              </div>
            )}
            <div className={cn(styles.settingsSwitchRow, styles.teacherPacedUpdates)}>
              <Switch
                checked={settings.autoHide}
                id="autoHide"
                label={I18n.t('auto_hide_slide_show_toolbar_label')}
                tooltip={I18n.t('auto_hide_slide_show_toolbar_tooltip')}
                onChange={(newValue) => {
                  update('autoHide', newValue);
                }}
              />
            </div>
            {autoplayAudio && slidesHaveAutoplayAudioOrVideo(slides) && (
              <div className={cn(styles.settingsSwitchRow, styles.teacherPacedUpdates)}>
                <Switch
                  checked={autoplayMediaSetting(slideShow.id, autoplayMediaSettings)}
                  id="autoplayMedia"
                  label={I18n.t('autoplay_toggle_label')}
                  onChange={toggleAutoplayMediaEnabled}
                />
              </div>
            )}
          </React.Fragment>
        )}
        toggleText={<i aria-hidden="true" className={`fa fa-ellipsis-v ${styles.settingsIcon}`} />}
      />
    );
  }

  return (
    <Dropdown
      ariaLabel={I18n.t('slide_show_settings')}
      buttonClass={styles.settingsButton}
      containerClass={styles.settingsDropdownContainer}
      dropdownClass={styles.settingsDropdown}
      dropdownPosition="top-end"
      render={({ isOpen }) => (
        <React.Fragment>
          <p className={cn(styles.dropdownHeader, styles.legacy)}>Settings</p>
          {slideShowFromStore && slideShowFromStore.hasSlideConditions && canViewSlideConditionsToggle && (
            <div className={cn(styles.settingsSwitchRow, styles.legacy)}>
              <Switch
                checked={settings.overrideSlideConditions}
                id="overrideSlideConditions"
                label={I18n.t('slide_conditions_override')}
                tooltip={I18n.t('slide_conditions_override_tooltip')}
                onChange={newValue => update('overrideSlideConditions', newValue)}
              />
            </div>
          )}
          <div className={cn(styles.settingsSwitchRow, styles.legacy)}>
            <Switch
              checked={settings.autoHide}
              id="autoHide"
              label={I18n.t('auto_hide_slide_show_toolbar_label')}
              tooltip={I18n.t('auto_hide_slide_show_toolbar_tooltip')}
              onChange={(newValue) => {
                update('autoHide', newValue);
              }}
            />
          </div>
          {autoplayAudio && slidesHaveAutoplayAudioOrVideo(slides) && (
            <div className={cn(styles.settingsSwitchRow, styles.legacy)}>
              <Switch
                checked={autoplayMediaSetting(slideShow.id, autoplayMediaSettings)}
                id="autoplayMedia"
                label={I18n.t('autoplay_toggle_label')}
                onChange={toggleAutoplayMediaEnabled}
              />
            </div>
          )}
          {!inIframe() && userType !== 'Student' && activityType !== 'marketing' && !isProfessionalDevelopment && (
            <SlideShowEditButton {...slideShowEditButtonProps} slideShow={slideShow} />
          )}
          {isOpen && showResetButton() && <ResetSlideShowButton />}
          <FullScreen {...fullScreenProps} />
        </React.Fragment>
      )}
      toggleText={<i aria-hidden="true" className={`fa fa-ellipsis-v ${styles.settingsIcon}`} />}
    />
  );
};

Settings.defaultProps = {
  settings: {},
};

Settings.propTypes = {
  fullScreenProps: FullScreenPropTypes.isRequired,
  settings: PropTypes.object,
  slideShow: PropTypes.object.isRequired,
  slideShowEditButtonProps: SlideShowEditButtonPropTypes.isRequired,
  toggleAutoplayMediaEnabled: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
};

export default Settings;
